import * as React from 'react';

import { Input, Text } from '@components/common';
import { ListViewSVG, UnmoderatedSVG } from '@components/svgs';

import { CardHeader } from './CardHeader';

type EditableStudyFields = Pick<Study, 'research_goal' | 'title' | 'incentive' | 'participant_limit'> & {
  respondent_job_titles?: string[];
  respondent_industries?: string[];
  respondent_skills?: string[];
};

interface StudyDetailsProps {
  study: Pick<Study, 'id'> & EditableStudyFields;
  method?: string;
  onFieldChange: (
    field: string,
    value: string | number | null | { jobs?: string[]; industries?: string[]; skills?: string[] }
  ) => void;
}

const renderField = (
  label: string,
  field: keyof EditableStudyFields,
  value: string | number | null | undefined,
  type: 'text' | 'number' | 'currency' = 'text',
  isEditing: boolean,
  onFieldChange: (field: keyof EditableStudyFields, value: string | number | null) => void
) => {
  if (!isEditing) {
    return (
      <div>
        <Text bold className='text-sm text-gray-600'>
          {label}
        </Text>
        <Text className='text-sm'>{type === 'currency' && value ? `$${Number(value)}` : value || '-'}</Text>
      </div>
    );
  }

  return (
    <div>
      <Text bold className='text-sm text-gray-600'>
        {label}
      </Text>
      <Input
        type={type === 'currency' ? 'number' : type}
        value={value?.toString() || ''}
        onChange={(value: string) => onFieldChange(field, value)}
        className='mt-1 w-full'
        placeholder={`Enter ${label.toLowerCase()}`}
      />
    </div>
  );
};

export const StudyDetails: React.FC<StudyDetailsProps> = ({ study, method, onFieldChange }) => {
  const [isEditing, setIsEditing] = React.useState(false);

  return (
    <div className='relative rounded-lg border bg-white p-6'>
      <button
        onClick={() => setIsEditing(!isEditing)}
        className='absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100'
        aria-label={isEditing ? 'Done editing' : 'Edit study details'}
      >
        {isEditing ? (
          <span className='text-sm font-medium text-gray-700'>Done</span>
        ) : (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-5 w-5 text-gray-500'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z'
            />
          </svg>
        )}
      </button>
      <CardHeader icon={<ListViewSVG />} title='Plan' />
      <div className='space-y-4'>
        {renderField('Title', 'title', study.title, 'text', isEditing, onFieldChange)}
        {renderField('Research Goal', 'research_goal', study.research_goal, 'text', isEditing, onFieldChange)}
        {!isEditing && (
          <div>
            <Text bold className='text-sm text-gray-600'>
              Method
            </Text>
            <div className='mt-1 flex items-center gap-2'>
              {method ? (
                <>
                  <UnmoderatedSVG className='h-5 w-5 text-gray-500' />
                  <Text className='text-sm'>Unmoderated usability test</Text>
                </>
              ) : (
                ''
              )}
            </div>
          </div>
        )}
        {renderField('Incentive', 'incentive', study.incentive, 'currency', isEditing, onFieldChange)}
        {renderField(
          'Participant Limit',
          'participant_limit',
          study.participant_limit,
          'number',
          isEditing,
          onFieldChange
        )}
      </div>
    </div>
  );
};

export type { EditableStudyFields };
